import React from 'react';
import { Link } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { ObjectCardTile } from '../../../components/layout/ObjectCard';
import { formatCurrency } from '../../../utils/currency';
import { formatLocalizedNumber } from '../../../utils/numberFormatting';
import type {
  ForecastDeliverySource,
  ForecastVsActual,
  ForecastVsActualOrder,
  ForecastVsActualOrderTotals,
  ForecastVsActualPurchaseTotals,
  IncomeStatementOutsidePlanOrder,
} from './incomeStatementApi';
import { CostGapTag, gapKindForReason } from '../CostGapTag';
import { CostPartialMark, ReportLongTable } from '../ReportTab';
import { monthShortLabel } from './incomeStatement';

// WP6 "Prognoza a wynik" (docs/decisions/2026-09-24-wp6-prognoza-a-wynik.md).
// Renders the backend block as is: which order is forecast or actual, and
// every amount, is decided by incomeStatementService. The ZK money is the
// company result (decision 2026-10-29-zysk-przy-sprzedazy): an incomplete cost
// is its known part with a mark, never zero pretending to be complete; ZD
// purchases are information and are never added to the ZK cost of goods.

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const K = 'reports:profitability.incomeStatement.forecastVsActual';

interface Props {
  block: ForecastVsActual;
  /** Orders in the period that are not forecast because something is missing (labelled with the reason). */
  outsideOrders?: IncomeStatementOutsidePlanOrder[];
  /** Period of the block, in the title of the „Razem” row. */
  periodLabel: string;
  language: string;
  t: TranslateFn;
}

function money(value: number | null | undefined): string {
  return value == null ? '—' : formatCurrency(value);
}

// The month table (8 money columns) fits a 1280 px card in whole złoty on one
// line; the exact amount stays in `title`.
function Whole({ value }: { value: number | null | undefined }) {
  if (value == null) return <>—</>;
  return <span className="forecast-vs-actual__amount" title={formatCurrency(value)}>{`${formatLocalizedNumber(Math.round(value), 0, 'pl')} zł`}</span>;
}

function signedMoney(value: number): string {
  const formatted = formatCurrency(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;
  return formatted;
}

function monthLabel(month: string | null, language: string): string {
  return month ? monthShortLabel(month, language) : '—';
}

function CostUnknown({ label, hint }: { label: string; hint: string }) {
  return (
    <Tooltip title={hint}>
      <span className="forecast-vs-actual__unknown" data-testid="forecast-vs-actual-cost-unknown">{label}</span>
    </Tooltip>
  );
}

// Owner decision 2026-09-30: an incomplete cost is summed as its known part and
// marked — the shared report mark (ReportTab.tsx).
function PartialMark({ hint, testId = 'forecast-vs-actual-cost-partial' }: { hint: string; testId?: string }) {
  return <CostPartialMark hint={hint} testId={testId} />;
}

// SZ.9: a purchase sum with a draft ZD counted only in its known part.
function PurchaseAmount({ totals, t }: { totals: ForecastVsActualPurchaseTotals; t: TranslateFn }) {
  return (
    <>
      <Whole value={totals.amountPLN} />
      {totals.pendingCostCount > 0 && <PartialMark hint={t(`${K}.purchaseCostPartialHint`)} testId="forecast-vs-actual-purchase-partial" />}
    </>
  );
}

// Orders of the period that are not in the forecast, each with the reason as a label
// (for example "Brak wprowadzonych cen sprzedaży"), so nothing disappears without a trace.
function OutsideForecastOrders({ orders, language, t }: {
  orders: IncomeStatementOutsidePlanOrder[]; language: string; t: TranslateFn;
}) {
  if (orders.length === 0) return null;
  return (
    <div className="forecast-vs-actual__outside" data-testid="forecast-vs-actual-outside">
      <h5>{t(`${K}.outside.title`, { count: orders.length })}</h5>
      <p className="forecast-vs-actual__outside-hint">{t(`${K}.outside.hint`)}</p>
      <ReportLongTable rowCount={orders.length} testId="forecast-vs-actual-outside-rows">
        <table>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id} data-order-id={order.id}>
                <td><Link to={`/app/orders/${order.id}`}>{order.displayOrderNumber}</Link></td>
                <td>{order.customerName}</td>
                <td>{monthLabel(order.plannedDate ? order.plannedDate.slice(0, 7) : null, language)}</td>
                <td>
                  <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                    {order.reviewReasons.map((reason) => {
                      const kind = gapKindForReason(reason);
                      return kind
                        ? <CostGapTag key={reason} kind={kind} t={t} />
                        : <Tag key={reason}>{t(`reports:profitability.incomeStatement.outsidePlan.reasons.${reason}`, { defaultValue: reason })}</Tag>;
                    })}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportLongTable>
    </div>
  );
}

function TotalsCells({ totals, t }: { totals: ForecastVsActualOrderTotals; t: TranslateFn }) {
  return (
    <>
      <td><Whole value={totals.revenue} /></td>
      <td>
        {totals.totalCost == null
          ? <CostUnknown label={t(`${K}.costUnknown`)} hint={t(`${K}.pendingCount`, { count: totals.pendingCostOrderCount })} />
          : <><Whole value={totals.totalCost} />{totals.pendingCostOrderCount > 0 && <PartialMark hint={t(`${K}.costPartialHint`)} />}</>}
      </td>
      <td className={totals.margin != null && totals.margin < 0 ? 'is-negative' : ''}><Whole value={totals.margin} /></td>
    </>
  );
}

function OrderIdentity({ order, t }: { order: ForecastVsActualOrder; t: TranslateFn }) {
  return (
    <div className="forecast-vs-actual__identity">
      <Link to={`/app/orders/${order.id}`}>{order.displayOrderNumber}</Link>
      <span className="forecast-vs-actual__meta">
        <span>{order.customer?.name || t(`${K}.noCustomer`)}</span>
        {' · '}
        <span>{t(`orders:statusOptions.${order.status}`, { defaultValue: order.status })}</span>
      </span>
    </div>
  );
}

// Caption of a partial forecast row: a part on trucks is delivered in the
// month, the part on no truck only sits in the ZK forecast month.
const DELIVERY_SHARE_KEY: Record<ForecastDeliverySource, string> = {
  TRUCK: 'deliveryShare',
  NO_TRUCK: 'deliveryShareNoTruck',
  MIXED: 'deliveryShareMixed',
};

function DeltaCell({ order, t }: { order: ForecastVsActualOrder; t: TranslateFn }) {
  if (!order.planned) return <td className="forecast-vs-actual__muted">{t(`${K}.noSnapshot`)}</td>;
  const delta = order.delta?.margin ?? null;
  return (
    <td className={delta != null && delta < 0 ? 'is-negative' : ''}>
      <div className="forecast-vs-actual__stack">
        <span>{delta == null ? '—' : signedMoney(delta)}</span>
        <span className="forecast-vs-actual__meta">
          {order.planned.margin == null
            ? t(`${K}.plannedCostUnknown`)
            : t(`${K}.plannedMargin`, { amount: money(order.planned.margin) })}
        </span>
        {order.deliveryShare != null && (
          <span className="forecast-vs-actual__meta">{t(`${K}.deltaWholeOrder`)}</span>
        )}
      </div>
    </td>
  );
}

function IssuedOverInvoiceNote({ order, t }: { order: ForecastVsActualOrder; t: TranslateFn }) {
  if (order.issuedOverInvoiceCostPLN == null) return null;
  return (
    <span className="forecast-vs-actual__meta" data-testid="forecast-vs-actual-issued-over-invoice-order">
      {t(`${K}.issuedOverInvoiceOrder`, { amount: money(order.issuedOverInvoiceCostPLN) })}
    </span>
  );
}

function ActualCostCell({ order, t }: { order: ForecastVsActualOrder; t: TranslateFn }) {
  if (order.totalCost != null && !order.costPending) {
    if (order.issuedOverInvoiceCostPLN == null) return <td>{money(order.totalCost)}</td>;
    return (
      <td>
        <div className="forecast-vs-actual__stack">
          <span>{money(order.totalCost)}</span>
          <IssuedOverInvoiceNote order={order} t={t} />
        </div>
      </td>
    );
  }
  return (
    <td>
      <div className="forecast-vs-actual__stack">
        {order.totalCost == null
          ? <CostUnknown label={t(`${K}.ledgerPending`)} hint={t(`${K}.ledgerPendingHint`)} />
          : (
            <span className="forecast-vs-actual__amount">
              {money(order.totalCost)}
              <PartialMark hint={`${t(`${K}.ledgerPending`)}. ${t(`${K}.ledgerPendingHint`)}`} testId="forecast-vs-actual-ledger-partial" />
            </span>
          )}
        {order.unissued && (
          <span className="forecast-vs-actual__meta" data-testid="forecast-vs-actual-unissued">
            {order.unissued.costUnknown || order.unissued.averageCostPLN == null
              ? t(`${K}.unissuedUnknown`)
              : t(`${K}.unissuedEstimate`, { amount: money(order.unissued.averageCostPLN) })}
          </span>
        )}
        <IssuedOverInvoiceNote order={order} t={t} />
      </div>
    </td>
  );
}

function OrdersTable({ orders, basis, language, t }: {
  orders: ForecastVsActualOrder[];
  basis: 'forecast' | 'actual';
  language: string;
  t: TranslateFn;
}) {
  return (
    <ReportLongTable rowCount={orders.length} testId="forecast-vs-actual-orders-rows">
      <div className="profitability-income-firms-wrap">
        <table className="profitability-income-firms forecast-vs-actual__table">
          <thead>
            <tr>
              <th>{t(`${K}.cols.order`)}</th>
              <th>{t(`${K}.${basis}.colMonth`)}</th>
              <th>{t(`${K}.cols.revenue`)}</th>
              <th>{t(`${K}.cols.cost`)}</th>
              <th>{t(`${K}.cols.margin`)}</th>
              <th>{t(`${K}.cols.delta`)}</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={6} className="profitability-income-empty">{t(`${K}.${basis}.empty`)}</td></tr>
            ) : orders.map((order) => (
              <tr key={`${order.id}-${order.month ?? ''}`} data-order-id={order.id}>
                <td><OrderIdentity order={order} t={t} /></td>
                <td>
                  {monthLabel(order.month, language)}
                  {order.deliveryShare != null && (
                    <span className="forecast-vs-actual__share" data-testid="forecast-vs-actual-share">
                      {t(`${K}.forecast.${DELIVERY_SHARE_KEY[order.deliverySource ?? 'TRUCK']}`, { percent: Math.round(order.deliveryShare * 100) })}
                    </span>
                  )}
                </td>
                <td>{money(order.revenue)}</td>
                {basis === 'actual' ? <ActualCostCell order={order} t={t} /> : (
                  <td>
                    {order.totalCost == null
                      ? <CostUnknown label={t(`${K}.costUnknown`)} hint={t(`${K}.costUnknownHint`)} />
                      : <>{money(order.totalCost)}{order.costPending && <> <CostGapTag kind="NO_COST_OF_SALES" t={t} /></>}</>}
                  </td>
                )}
                <td className={order.margin != null && order.margin < 0 ? 'is-negative' : ''}>
                  <span className="forecast-vs-actual__amount">
                    {money(order.margin)}
                    {order.costPending && order.margin != null && <PartialMark hint={t(`${K}.costPartialHint`)} testId="forecast-vs-actual-margin-partial" />}
                  </span>
                </td>
                <DeltaCell order={order} t={t} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ReportLongTable>
  );
}

const ForecastVsActualSection: React.FC<Props> = ({ block, outsideOrders = [], periodLabel, language, t }) => {
  // A month with no order and no purchase on either side is an empty row;
  // height is the scarce dimension (DESIGN.md), the total row stays.
  const activeMonths = block.months.filter((month) => (
    month.forecast.orders.orderCount > 0 || month.actual.orders.orderCount > 0
    || month.forecast.purchases.count > 0 || month.actual.purchases.count > 0
  ));

  return (
    <section
      className="profitability-income__section forecast-vs-actual"
      aria-label={t(`${K}.kicker`)}
      data-testid="forecast-vs-actual"
    >
      <div className="profitability-income__section-head">
        <div>
          <div className="profitability-income__section-title">
            <ObjectCardTile tone="orange" size="sm"><SwapOutlined /></ObjectCardTile>
            <h3>{t(`${K}.title`)}</h3>
          </div>
          <p title={t(`${K}.hint`)}>{t(`${K}.hint`)}</p>
        </div>
      </div>

      <div className="profitability-income__panel">
        <ReportLongTable rowCount={activeMonths.length} testId="forecast-vs-actual-months-rows">
          <div className="profitability-income-firms-wrap">
            <table className="profitability-income-firms forecast-vs-actual__months" data-testid="forecast-vs-actual-months">
              <thead>
                <tr className="forecast-vs-actual__groups">
                  <th rowSpan={2}>{t(`${K}.months.colMonth`)}</th>
                  <th colSpan={3}>{t(`${K}.months.groupForecast`)}</th>
                  <th colSpan={3}>{t(`${K}.months.groupActual`)}</th>
                  <th colSpan={2}>
                    {t(`${K}.months.groupPurchases`)}
                    <span className="forecast-vs-actual__group-note">{t(`${K}.months.purchasesNote`)}</span>
                  </th>
                </tr>
                <tr>
                  <th>{t(`${K}.months.colRevenue`)}</th>
                  <th>{t(`${K}.months.colCost`)}</th>
                  <th>{t(`${K}.months.colMargin`)}</th>
                  <th>{t(`${K}.months.colRevenue`)}</th>
                  <th>{t(`${K}.months.colCost`)}</th>
                  <th>{t(`${K}.months.colMargin`)}</th>
                  <th>{t(`${K}.months.colPurchaseForecast`)}</th>
                  <th>{t(`${K}.months.colPurchaseActual`)}</th>
                </tr>
              </thead>
              <tbody>
                {activeMonths.length === 0 && (
                  <tr><td colSpan={9} className="profitability-income-empty">{t(`${K}.months.empty`)}</td></tr>
                )}
                {activeMonths.map((month) => (
                  <tr key={month.month}>
                    <td>{monthShortLabel(month.month, language)}</td>
                    <TotalsCells totals={month.forecast.orders} t={t} />
                    <TotalsCells totals={month.actual.orders} t={t} />
                    <td><PurchaseAmount totals={month.forecast.purchases} t={t} /></td>
                    <td><PurchaseAmount totals={month.actual.purchases} t={t} /></td>
                  </tr>
                ))}
                <tr className="forecast-vs-actual__total">
                  <td title={`${t(`${K}.months.total`)} · ${periodLabel}`}>{t(`${K}.months.total`)}</td>
                  <TotalsCells totals={block.totals.forecast.orders} t={t} />
                  <TotalsCells totals={block.totals.actual.orders} t={t} />
                  <td><PurchaseAmount totals={block.totals.forecast.purchases} t={t} /></td>
                  <td><PurchaseAmount totals={block.totals.actual.purchases} t={t} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </ReportLongTable>
      </div>

      <section className="profitability-income__panel forecast-vs-actual__part" aria-label={t(`${K}.forecast.title`)}>
        <header className="forecast-vs-actual__part-head">
          <h4>
            {t(`${K}.forecast.title`)}
            {' '}
            {block.totals.forecast.orders.pendingCostOrderCount > 0 && (
              <Tag color="warning">{t(`${K}.pendingCount`, { count: block.totals.forecast.orders.pendingCostOrderCount })}</Tag>
            )}
          </h4>
          <p>{t(`${K}.forecast.hint`)}</p>
        </header>
        <OrdersTable orders={block.orders.forecast} basis="forecast" language={language} t={t} />
        <OutsideForecastOrders orders={outsideOrders} language={language} t={t} />
      </section>

      <section className="profitability-income__panel forecast-vs-actual__part" aria-label={t(`${K}.actual.title`)}>
        <header className="forecast-vs-actual__part-head">
          <h4>
            {t(`${K}.actual.title`)}
            {' '}
            {block.totals.actual.orders.pendingCostOrderCount > 0 && (
              <Tag color="warning">{t(`${K}.pendingCount`, { count: block.totals.actual.orders.pendingCostOrderCount })}</Tag>
            )}
          </h4>
          <p>{t(`${K}.actual.hint`)}</p>
          {block.issuedOverInvoice.orderCount > 0 && (
            <Tooltip title={t(`${K}.actual.issuedOverInvoiceHint`)}>
              <p className="forecast-vs-actual__unknown forecast-vs-actual__notice" data-testid="forecast-vs-actual-issued-over-invoice">
                {t(`${K}.actual.issuedOverInvoice`, {
                  amount: money(block.issuedOverInvoice.costPLN),
                  count: block.issuedOverInvoice.orderCount,
                })}
              </p>
            </Tooltip>
          )}
        </header>
        <OrdersTable orders={block.orders.actual} basis="actual" language={language} t={t} />
      </section>
    </section>
  );
};

export default ForecastVsActualSection;
